import { FC, memo, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { TermInsuranceProduct } from '@common/types';
import {
  checkRiderPremiumOverlap,
  checkRiderPremiumOverlapWithAddOn,
  TermAddOn,
  useTermQuoteStore
} from '@store/termquote';
import { AddOnPremiumRate } from '@termquote/blocks';
import {
  PREMIUM_OVERFLOW_MSG,
  PREMIUM_OVERFLOW_TITLE,
  PREMIUM_OVERLAP_MSG,
  PREMIUM_OVERLAP_TITLE,
  TERM_FAST_QUOTE_STEPS,
  TERM_QUOTE_SLUGS
} from '@termquote/constants';
import { useGenTermPath } from '@termquote/utils';
import {
  Box,
  Checkbox,
  Flex,
  Grid,
  showNotification,
  Skeleton,
  Stack,
  Text
} from '@ui';
import { convertToINR } from '@utils';
import { TERM_INSURANCE_PRODUCTS } from '@utils/constants';

// import { AddOnSubOptions } from './AddOnSubOptions';

interface TermAddOnItemProps {
  addOn: TermAddOn;
  productFinalized?: boolean;
  showInclBaseWithTooltip?: boolean;
}

const TermAddOnItem: FC<TermAddOnItemProps> = memo(
  ({ addOn, productFinalized, showInclBaseWithTooltip }) => {
    const navigate = useNavigate();
    const {
      loadingPremium,
      toggleAddOn,
      derived,
      premium,
      step,
      setCurrentAddOn,
      disableWopMaxLife,
      plan
    } = useTermQuoteStore((state) => ({
      loadingPremium: state.loadingPremium,
      premium: state.premium,
      step: state.step,
      derived: state.derived,
      toggleAddOn: state.toggleAddOn,
      setCurrentAddOn: state.setCurrentAddOn,
      disableWopMaxLife: state.disableWopMaxLife,
      plan: state.plan
    }));
    const { age: userAge } = derived || {};
    const { policyTerm } = premium || {};

    const addOnTerm = useMemo(() => {
      if (userAge && policyTerm && addOn.included) {
        if (addOn.additionalPremium?.term) {
          return `Till age ${addOn.additionalPremium?.term + userAge}`;
        }

        return `Till age ${policyTerm + userAge}`;
      }

      return '';
    }, [userAge, policyTerm, addOn]);

    const overlap = checkRiderPremiumOverlap(premium);

    const overflow =
      addOn.preventOverflow &&
      addOn.additionalPremium &&
      addOn.additionalPremium?.base > premium.basePlan?.base;

    const preventActions = overflow || overlap;

    const handleClickToEnable = () => {
      setCurrentAddOn(addOn.key);
      if (step === TERM_FAST_QUOTE_STEPS.CHECKOUT) {
        navigate(useGenTermPath(TERM_QUOTE_SLUGS.plan));
      }
    };

    const variantName = useMemo(() => {
      if (addOn.selectedVariant && addOn.selectedVariant.variant) {
        const variantDetails = addOn.variants?.find(
          (item) => item.type === addOn.selectedVariant?.variant
        );
        return `${variantDetails?.name}・`;
      }
      return null;
    }, [addOn]);

    const shouldShowAddon = true;

    const showCover = addOn.cover && addOn.cover > 0;

    /**
     * check if adding the addon can cause an overlap
     * @param checked
     */
    const onToggleAddOn = (checked: boolean) => {
      if (step === TERM_FAST_QUOTE_STEPS.CHECKOUT) {
        if (checked) {
          const latestOverlap = checkRiderPremiumOverlapWithAddOn(
            premium,
            addOn.key
          );
          if (latestOverlap) {
            showNotification({
              title: PREMIUM_OVERLAP_TITLE,
              message: PREMIUM_OVERLAP_MSG,
              color: 'danger'
            });
            return;
          }

          if (overflow) {
            showNotification({
              title: PREMIUM_OVERFLOW_TITLE,
              message: PREMIUM_OVERFLOW_MSG,
              color: 'danger'
            });
            return;
          }
        }
      }
      // toggle addOn if there is no overlap
      toggleAddOn(addOn.key, checked);
    };

    const getMLIWopRiderStatus = () => {
      if (
        [
          TERM_INSURANCE_PRODUCTS.SMART_TOTAL_ELITE,
          TERM_INSURANCE_PRODUCTS.SMART_TERM_PLAN_PLUS
        ].includes(plan.product as TermInsuranceProduct)
      ) {
        return disableWopMaxLife;
      }
      return false;
    };

    return shouldShowAddon ? (
      <Stack w="100%" mb={productFinalized ? 'xs' : 0} gap="xs">
        <Grid align="center">
          <Grid.Col span={{ base: 7, sm: 8 }}>
            <Flex align="center">
              {!productFinalized ? (
                <Checkbox
                  disabled={
                    (addOn.isFree && addOn.isEligible && !addOn.isOptional) ||
                    (addOn.additionalPremium &&
                      addOn.additionalPremium?.base === 0) ||
                    (addOn.inclusiveInBasePremium && !addOn.cover) ||
                    getMLIWopRiderStatus()
                  }
                  name={addOn.name}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    //All the existing add ons
                    onToggleAddOn(checked);
                  }}
                  checked={addOn.included}
                />
              ) : null}
              <Flex ml={!productFinalized ? 10 : 0} align="center">
                <Text
                  lightColor={
                    !preventActions
                      ? !addOn.isFree
                        ? 'text'
                        : 'secondary'
                      : 'dimmed'
                  }
                  darkColor="text.2"
                  fz={14}
                >
                  {addOn.name}
                </Text>
              </Flex>
            </Flex>
          </Grid.Col>
          <Grid.Col span={{ base: 5, sm: 4 }}>
            <Flex justify="flex-end" w="100%">
              {!loadingPremium ? (
                !productFinalized &&
                !addOn.isFree &&
                !addOn.additionalPremium?.base &&
                !addOn.inclusiveInBasePremium ? (
                  <Box ml={28} mt={-4}>
                    <Text
                      style={{ cursor: 'pointer' }}
                      onClick={handleClickToEnable}
                      c="primary.5"
                    >
                      Configure
                    </Text>
                  </Box>
                ) : (
                  <Text c="Secondary" ta="right">
                    <AddOnPremiumRate
                      addOn={addOn}
                      showInclBaseWithTooltip={showInclBaseWithTooltip}
                      showFreeAsText
                    />
                  </Text>
                )
              ) : (
                <Flex justify="flex-end">
                  <Skeleton variant="rect" width={50} />
                </Flex>
              )}
            </Flex>
          </Grid.Col>
        </Grid>
        {!productFinalized && !addOn.isFree ? (
          showCover ? (
            <Text fz={13} c="secondary" ml={20}>
              ({variantName} Cover {convertToINR(addOn.cover, 2)}
              {addOn.included ? `・${addOnTerm}` : null})
            </Text>
          ) : null
        ) : null}
      </Stack>
    ) : null;
  }
);

export { TermAddOnItem };
